import { Injectable } from '@angular/core';
import * as firebase from 'firebase';
import 'rxjs/add/operator/take';
import { ENV } from '@app/env';
import { HttpClient } from '@angular/common/http';
import { UtilitiesProvider } from './utilities';
import { Observable } from 'rxjs/Observable';
import { AngularFirestore } from 'angularfire2/firestore';
import { Platform } from 'ionic-angular';
import { map, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as userSelectors from '../ngrx/reducers/auth.reducer';
import { IUser } from 'models/user.model';
import { isEmpty } from 'lodash';

declare var Notification: any;

@Injectable()
export class NotificationData {

  private firebaseMessaging = firebase.messaging()

  constructor(
    private afStore: AngularFirestore,
    private platform: Platform,
    private http: HttpClient,
    private store: Store<{}>,
    private utilities: UtilitiesProvider
  ) { }

  public canShowNotificationPopUp(): boolean {
    if ('Notification' in window) {
      switch (Notification.permission) {
        case 'default':
          return true;
        case 'granted':
          return null;
        case 'denied':
          return false;
      }
    }

    return false;
  }

  /**
   * Get the user information
   */
  public getUserInformation(): Observable<IUser> {
    return this.store.select(userSelectors.getUser);
  }

  public userRequestPermission(): Observable<any> {
    return this.getUserInformation().pipe(
      switchMap((user: IUser) => {
        if (user) {
          return this.requestDevicePermissionFCM().pipe(
            switchMap(() => {
              return this.getUserTokenFromFirebase(user.info.id)
                .pipe(switchMap((dataUser: any) => {
                  return this.getUserTokenFCM()
                    .pipe(switchMap((token) => {
                      // User no has token previous
                      if (dataUser === null) {
                        return this.saveUserTokenFCM(user.info.id, token);
                      }
                      else {
                        if (dataUser.token !== token) {
                          return this.updateUserTopics(user.info.id, dataUser.token, token)
                            .pipe(switchMap(() => {
                              return this.saveUserTokenFCM(user.info.id, token)
                            }))
                        }
                        else {
                          return Observable.of({});
                        }
                      }
                    }))
                }))
            }),
            switchMap(() => {
              return this.addTopicToUserFirestore(user.info.id, ENV.client);
            })
          );
        }
        return Observable.of({});
      })
    )
  }

  public userGetBrowserToken(): Observable<any> {
    return this.getUserInformation().pipe(
      switchMap((user: IUser) => {
        if (user) {
          return this.getUserTokenFromFirebase(user.info.id)
            .pipe(
              switchMap((dataUser: any) => {
                return this.getUserTokenFCM()
                  .pipe(
                    switchMap((token) => {
                      this.utilities.consoleDecorator('token', token);
                      // User no has token previous
                      if (dataUser === null) {
                        return this.saveUserTokenFCM(user.info.id, token);
                      }
                      else {
                        if (dataUser.token !== token) {
                          return this.updateUserTopics(user.info.id, dataUser.token, token)
                            .pipe(switchMap(() => {
                              return this.saveUserTokenFCM(user.info.id, token)
                            }))
                        }
                        else {
                          return Observable.of({});
                        }
                      }
                    })
                  );
              })
            );
        } else {
          return Observable.of({});
        }
      })
    )
  }

  /**
   * Request Device Permission FCM
   */
  public requestDevicePermissionFCM() {
    return Observable.fromPromise(this.firebaseMessaging.requestPermission());
  }

  /**
   * Retrieve the token from firebase FCM
   */
  public getUserTokenFCM(): Observable<string> {
    if (this.firebaseMessaging) {
      return Observable.fromPromise(this.firebaseMessaging.getToken())
    }
    return Observable.of();
  }

  /**
   * Save the user token FCM on Firestore DB with platform and creation_date properties.
   * @param userId
   * @param token
   */
  public saveUserTokenFCM(userId: string, token: string) {
    return this.afStore.collection(ENV.client).doc('users-tokens').collection(userId).doc(userId).set({
      id: userId,
      userId: userId,
      platform: this.platform.platforms(),
      token: token,
      create_at: firebase.firestore.FieldValue.serverTimestamp(),
    })
  }



  /** Get the user token from firestore
   * @param userId
   * @param token
   * **/
  public getUserTokenFromFirebase(userId: string) {
    return this.afStore.collection(ENV.client).doc('users-tokens').collection(userId, ref => {
      return ref.where('userId', '==', userId)
    }).valueChanges().pipe(
      map((data) => {
        return !isEmpty(data) ? data[0] : null;
      })
    )
  }

  /**
   * Add a topic to the user on Firestore DB
   * @param userId
   * @param topic
   */
  public addTopicToUserFirestore(userId: string, topic: string): Observable<any> {
    return Observable.fromPromise(this.afStore.collection(ENV.client).doc('users-topics').collection(userId).doc(topic).set({
      id: topic,
      userId: userId,
      topic: topic,
      create_at: firebase.firestore.FieldValue.serverTimestamp()
    }).catch(error => this.utilities.consoleError('Error topic', error)))
      .pipe(switchMap(() => {
        return this.getUserTokenFromFirebase(userId)
          .pipe(switchMap((userData: any) => {
            if (userData !== null) {
              return this.addTopicToUser(userData.token, topic)
            }
            else {
              return Observable.of({})
            }
          }))
      }));
  }

  public addTopicToUser(token: string, topic: string) {
    return this.http.post(`${ENV.backend}/notification/registerUserToTopic`,
      {
        token: token,
        topic: topic
      });
  }

  public updateUserTopics(userId: string, lastToken: string, newToken: string): Observable<any> {
    return this.http.post(`${ENV.backend}/notification/subscribeUserToTopics`,
      {
        userId: userId,
        clientId: ENV.client,
        lastToken: lastToken,
        newToken: newToken
      });
  }

  /**
   * TODO: Add support for delete topics
   */

  // TODO: Manage push notifications when the app is open
  // public receiveMessage() {
  //   this.messaging.onMessage((payload) => {

  //     this.utilities.consoleDecorator("Message received. ", payload);
  //     this.currentMessage.next(payload)
  //   });

  // }


}



// WEBPACK FOOTER //
// ./src/providers/notification.ts